import React, { useState, useEffect } from 'react';
import './FlashcardApp.css';

const FlashcardApp = () => {
  // 상태 관리
  const [targetLang, setTargetLang] = useState('영어');
  const [synonymLang, setSynonymLang] = useState('한국어');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentData, setCurrentData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSynonym, setShowSynonym] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [studyMode, setStudyMode] = useState('category'); // category 또는 unknown
  const [transitionEffect, setTransitionEffect] = useState(false);
  const [wordData, setWordData] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentEditWord, setCurrentEditWord] = useState(null);

  // 초기 데이터 로드
  useEffect(() => {
    loadDictionaryData();
  }, []);

  // 사전 데이터 로드
  const loadDictionaryData = async () => {
    try {
      setLoading(true);
      
      // 기본 데이터
      const defaultData = {
        "영어": {
          "한국어": {
            "일상": [
              { target: "hello", synonyms: ["안녕하세요", "안녕"], unknown: false },
              { target: "goodbye", synonyms: ["안녕히 가세요", "잘 가요"], unknown: false },
              { target: "thank you", synonyms: ["감사합니다", "고마워요"], unknown: false },
              { target: "please", synonyms: ["부탁합니다", "제발"], unknown: false },
              { target: "sorry", synonyms: ["죄송합니다", "미안합니다"], unknown: false }
            ],
            "학습": [
              { target: "book", synonyms: ["책", "서적"], unknown: false },
              { target: "pen", synonyms: ["펜", "볼펜"], unknown: false },
              { target: "pencil", synonyms: ["연필", "필기구"], unknown: false },
              { target: "notebook", synonyms: ["공책", "노트"], unknown: false },
              { target: "study", synonyms: ["공부", "학습"], unknown: false }
            ]
          }
        },
        "일본어": {
          "한국어": {
            "기초": [
              { target: "こんにちは", synonyms: ["안녕하세요", "안녕"], unknown: false },
              { target: "ありがとう", synonyms: ["감사합니다", "고마워요"], unknown: false },
              { target: "さようなら", synonyms: ["안녕히 가세요", "안녕"], unknown: false }
            ]
          }
        }
      };
      
      // 로컬 스토리지에서 데이터 확인
      const savedData = localStorage.getItem('flashcardData');
      const data = savedData ? JSON.parse(savedData) : defaultData;
      
      setWordData(data);
      setAvailableLanguages(Object.keys(data));
      setLoading(false);
      
    } catch (error) {
      console.error('데이터 로딩 실패:', error);
      setLoading(false);
    }
  };

  // 언어 선택이 변경되면 해당 언어의 단어 데이터 로드
  useEffect(() => {
    if (!loading && wordData[targetLang] && wordData[targetLang][synonymLang]) {
      // 선택된 언어 쌍의 카테고리 목록
      const categories = Object.keys(wordData[targetLang][synonymLang]);
      
      // 현재 선택된 카테고리가 없거나 유효하지 않으면 첫 번째 카테고리로 설정
      if (categories.length > 0 && (!selectedCategory || !categories.includes(selectedCategory))) {
        setSelectedCategory(categories[0]);
      }
    }
  }, [targetLang, synonymLang, loading, wordData, selectedCategory]);

  // 현재 선택된 카테고리 또는 모르는 단어 데이터 로드
  useEffect(() => {
    loadData();
  }, [selectedCategory, studyMode, wordData, targetLang, synonymLang]);

  // 데이터 로드 함수
  const loadData = () => {
    if (loading || !wordData[targetLang] || !wordData[targetLang][synonymLang]) {
      setCurrentData([]);
      return;
    }
    
    if (studyMode === 'unknown') {
      // 모든 카테고리에서 unknown 플래그가 true인 단어들만 필터링
      const unknownWords = [];
      Object.keys(wordData[targetLang][synonymLang]).forEach(category => {
        const categoryWords = wordData[targetLang][synonymLang][category];
        categoryWords.forEach(word => {
          if (word.unknown) {
            unknownWords.push({...word, category});
          }
        });
      });
      setCurrentData([...unknownWords]);
    } else {
      // 선택된 카테고리의 모든 단어
      if (wordData[targetLang][synonymLang][selectedCategory]) {
        setCurrentData([...wordData[targetLang][synonymLang][selectedCategory]]);
      } else {
        setCurrentData([]);
      }
    }
    setCurrentIndex(0);
    setShowSynonym(false);
  };

  // 자동 재생 관련 로직
  useEffect(() => {
    let timer;
    if (isAutoPlay && currentData.length > 0 && !editMode) {
      // 먼저 대상어만 보여줌
      setShowSynonym(false);
      
      // 3초 후에 동의어 표시
      timer = setTimeout(() => {
        setShowSynonym(true);
        
        // 동의어 표시 1초 후 다음 카드
        timer = setTimeout(() => {
          nextCard();
        }, 1000);
      }, 3000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAutoPlay, currentIndex, currentData, editMode]);

  // 데이터 저장 함수
  const saveData = () => {
    try {
      localStorage.setItem('flashcardData', JSON.stringify(wordData));
      alert('데이터가 저장되었습니다.');
    } catch (error) {
      console.error('데이터 저장 실패:', error);
      alert('데이터 저장에 실패했습니다.');
    }
  };

  // 다음 카드로 이동
  const nextCard = () => {
    if (currentData.length === 0 || editMode) return;
    
    setTransitionEffect(true);
    setTimeout(() => {
      if (currentIndex < currentData.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setCurrentIndex(0); // 마지막 카드면 처음으로 돌아감
      }
      setShowSynonym(false);
      setTransitionEffect(false);
    }, 300);
  };

  // 이전 카드로 이동
  const prevCard = () => {
    if (currentData.length === 0 || editMode) return;
    
    setTransitionEffect(true);
    setTimeout(() => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else {
        setCurrentIndex(currentData.length - 1); // 첫 카드면 마지막으로 이동
      }
      setShowSynonym(false);
      setTransitionEffect(false);
    }, 300);
  };

  // 단어 모르겠음 표시
  const markAsUnknown = () => {
    if (currentData.length === 0 || editMode) return;
    
    const updatedData = {...wordData};
    const currentWord = currentData[currentIndex];
    const category = studyMode === 'unknown' ? currentWord.category : selectedCategory;
    
    // 현재 단어를 unknown으로 표시
    if (updatedData[targetLang] && 
        updatedData[targetLang][synonymLang] && 
        updatedData[targetLang][synonymLang][category]) {
      
      updatedData[targetLang][synonymLang][category] = 
        updatedData[targetLang][synonymLang][category].map(word => {
          if (word.target === currentWord.target) {
            return {...word, unknown: true};
          }
          return word;
        });
      
      setWordData(updatedData);
      localStorage.setItem('flashcardData', JSON.stringify(updatedData));
      
      // 현재 모르는 단어 모드에서는 상태를 업데이트
      if (studyMode === 'category') {
        const updatedCurrentData = [...currentData];
        updatedCurrentData[currentIndex] = {...updatedCurrentData[currentIndex], unknown: true};
        setCurrentData(updatedCurrentData);
      }
    }
    
    nextCard();
  };

  // 언어 상호 전환
  const swapLanguages = () => {
    // 이미 선택한 언어들 사이에서만 전환
    const temp = targetLang;
    setTargetLang(synonymLang);
    setSynonymLang(temp);
    setIsAutoPlay(false);
  };

  // 랜덤으로 섞기
  const shuffleCards = () => {
    if (editMode) return;
    const shuffled = [...currentData].sort(() => Math.random() - 0.5);
    setCurrentData(shuffled);
    setCurrentIndex(0);
    setShowSynonym(false);
  };

  // 자동 재생 토글
  const toggleAutoPlay = () => {
    if (editMode) return;
    setIsAutoPlay(!isAutoPlay);
    setShowSynonym(false);
  };

  // 카테고리 변경
  const handleCategoryChange = (category) => {
    if (editMode) return;
    setSelectedCategory(category);
    setStudyMode('category');
    setIsAutoPlay(false);
  };

  // 모르는 단어 모드 전환
  const switchToUnknownMode = () => {
    if (editMode) return;
    setStudyMode('unknown');
    setIsAutoPlay(false);
  };

  // 사용 가능한 카테고리 목록 가져오기
  const getCategories = () => {
    if (!wordData[targetLang] || !wordData[targetLang][synonymLang]) {
      return [];
    }
    return Object.keys(wordData[targetLang][synonymLang]);
  };

  // 카테고리별 모르는 단어 수 계산
  const getUnknownCount = (category) => {
    if (!wordData[targetLang] || !wordData[targetLang][synonymLang] || !wordData[targetLang][synonymLang][category]) {
      return 0;
    }
    return wordData[targetLang][synonymLang][category].filter(word => word.unknown).length;
  };

  // 전체 모르는 단어 수 계산
  const getTotalUnknownCount = () => {
    if (!wordData[targetLang] || !wordData[targetLang][synonymLang]) {
      return 0;
    }
    
    let count = 0;
    Object.keys(wordData[targetLang][synonymLang]).forEach(category => {
      count += wordData[targetLang][synonymLang][category].filter(word => word.unknown).length;
    });
    return count;
  };

  // 파일 다운로드 함수
  const downloadData = () => {
    try {
      const dataStr = JSON.stringify(wordData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'flashcard-data.json';
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  // 파일 업로드 함수
  const uploadData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        setWordData(data);
        setAvailableLanguages(Object.keys(data));
        
        // 새로운 데이터에 맞게 언어 및 카테고리 설정
        const firstLang = Object.keys(data)[0];
        if (firstLang) {
          setTargetLang(firstLang);
          
          const secondLang = data[firstLang] ? Object.keys(data[firstLang])[0] : null;
          if (secondLang) {
            setSynonymLang(secondLang);
            
            const firstCategory = data[firstLang][secondLang] ? Object.keys(data[firstLang][secondLang])[0] : null;
            if (firstCategory) {
              setSelectedCategory(firstCategory);
            }
          }
        }
        
        // 데이터 로컬 스토리지 저장
        localStorage.setItem('flashcardData', JSON.stringify(data));
        alert('데이터가 성공적으로 업로드되었습니다.');
      } catch (error) {
        console.error('파일 업로드 실패:', error);
        alert('파일 업로드에 실패했습니다. 올바른 JSON 형식인지 확인하세요.');
      }
    };
    
    reader.readAsText(file);
  };
  
  // 편집 모드 토글
  const toggleEditMode = () => {
    if (currentData.length === 0) return;
    
    if (editMode) {
      // 편집 모드 종료
      setEditMode(false);
      setCurrentEditWord(null);
    } else {
      // 편집 모드 시작
      setEditMode(true);
      setCurrentEditWord({
        target: currentData[currentIndex].target,
        synonyms: [...currentData[currentIndex].synonyms],
        unknown: currentData[currentIndex].unknown
      });
    }
    
    setIsAutoPlay(false);
  };
  
  // 단어 편집 저장
  const saveEditedWord = () => {
    if (!currentEditWord || !editMode) return;
    
    const updatedData = {...wordData};
    const currentCategory = studyMode === 'unknown' ? currentData[currentIndex].category : selectedCategory;
    
    // 단어 업데이트
    if (updatedData[targetLang] && 
        updatedData[targetLang][synonymLang] && 
        updatedData[targetLang][synonymLang][currentCategory]) {
      
      updatedData[targetLang][synonymLang][currentCategory] = 
        updatedData[targetLang][synonymLang][currentCategory].map(word => {
          if (word.target === currentData[currentIndex].target) {
            return {...currentEditWord};
          }
          return word;
        });
      
      // 데이터 업데이트
      setWordData(updatedData);
      localStorage.setItem('flashcardData', JSON.stringify(updatedData));
      
      // 현재 데이터 업데이트
      const updatedCurrentData = [...currentData];
      updatedCurrentData[currentIndex] = {...currentEditWord};
      setCurrentData(updatedCurrentData);
      
      // 편집 모드 종료
      setEditMode(false);
      setCurrentEditWord(null);
    }
  };
  
  // 입력 필드 변경 처리
  const handleInputChange = (field, value, index) => {
    if (!currentEditWord) return;
    
    if (field === 'target') {
      setCurrentEditWord({...currentEditWord, target: value});
    } else if (field === 'synonym') {
      const updatedSynonyms = [...currentEditWord.synonyms];
      updatedSynonyms[index] = value;
      setCurrentEditWord({...currentEditWord, synonyms: updatedSynonyms});
    }
  };
  
  // 동의어 추가
  const addSynonym = () => {
    if (!currentEditWord) return;
    const updatedSynonyms = [...currentEditWord.synonyms, ''];
    setCurrentEditWord({...currentEditWord, synonyms: updatedSynonyms});
  };
  
  // 동의어 삭제
  const removeSynonym = (index) => {
    if (!currentEditWord || currentEditWord.synonyms.length <= 1) return;
    
    const updatedSynonyms = [...currentEditWord.synonyms];
    updatedSynonyms.splice(index, 1);
    setCurrentEditWord({...currentEditWord, synonyms: updatedSynonyms});
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-text">데이터 로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="flashcard-app">
      {/* 헤더 - 언어 선택 */}
      <header className="app-header">
        <h1>다국어 스피드 암기 학습</h1>
        <div className="language-selectors">
          <div className="select-group">
            <label>대상어:</label>
            <select 
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              className="language-select"
              disabled={editMode}
            >
              {availableLanguages.map(lang => (
                <option key={`target-${lang}`} value={lang}>{lang}</option>
              ))}
            </select>
          </div>
          
          <div className="select-group">
            <label>동의어:</label>
            <select 
              value={synonymLang}
              onChange={(e) => setSynonymLang(e.target.value)}
              className="language-select"
              disabled={editMode}
            >
              {availableLanguages
                .filter(lang => lang !== targetLang)
                .map(lang => (
                  <option key={`synonym-${lang}`} value={lang}>{lang}</option>
                ))
              }
            </select>
          </div>
          
          <button 
            onClick={swapLanguages}
            className="language-swap-btn"
            title="언어 전환"
            disabled={editMode}
          >
            전환
          </button>
        </div>
      </header>

      {/* 파일 작업 툴바 */}
      <div className="file-toolbar">
        <button
          onClick={downloadData}
          className="file-button download"
          title="현재 단어장 데이터를 JSON 파일로 내려받습니다."
        >
          단어장 내려받기
        </button>
        
        <label 
          className="file-button upload"
          title="JSON 형식의 단어장 파일을 업로드하여 현재 데이터를 교체합니다."
        >
          단어장 올리기
          <input
            type="file"
            accept=".json"
            onChange={uploadData}
            className="file-input"
          />
        </label>
        
        <button
          onClick={saveData}
          className="file-button save"
          title="현재 변경사항을 로컬 저장소에 저장합니다."
        >
          저장
        </button>
      </div>

      {/* 카테고리 가로 버튼 */}
      <div className="category-container">
        <div className="category-buttons">
          {getCategories().map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`category-button ${selectedCategory === category && studyMode === 'category' ? 'active' : ''}`}
              disabled={editMode}
            >
              {category}
              {getUnknownCount(category) > 0 && (
                <span className="badge">
                  {getUnknownCount(category)}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={switchToUnknownMode}
            className={`category-button ${studyMode === 'unknown' ? 'active' : ''}`}
            disabled={editMode}
          >
            모르는 단어
            <span className={`badge ${studyMode === 'unknown' ? 'light' : ''}`}>
              {getTotalUnknownCount()}
            </span>
          </button>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <main className="main-content">
        {/* 플래시카드 */}
        {currentData.length > 0 ? (
          <div className="flashcard-container">
            <div className="flashcard-wrapper">
              {!editMode ? (
                // 학습 모드 카드
                <div 
                  className={`flashcard ${transitionEffect ? 'transition' : ''}`}
                  onClick={() => !isAutoPlay && !editMode && setShowSynonym(!showSynonym)}
                >
                  {/* 대상어 */}
                  <div className="target-word">
                    {currentData[currentIndex]?.target}
                  </div>
                  
                  {/* 동의어 */}
                  <div className={`synonym-word ${!showSynonym ? 'hidden' : ''}`}>
                    {showSynonym && currentData[currentIndex]?.synonyms.join(", ")}
                  </div>
                </div>
              ) : (
                // 편집 모드 카드
                <div className="edit-card">
                  <h3 className="edit-title">단어 편집</h3>
                  
                  <div className="form-group">
                    <label className="form-label">
                      대상어 ({targetLang}):
                    </label>
                    <input
                      type="text"
                      value={currentEditWord?.target || ''}
                      onChange={(e) => handleInputChange('target', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      동의어 ({synonymLang}):
                    </label>
                    {currentEditWord?.synonyms.map((synonym, index) => (
                      <div key={index} className="synonym-input-group">
                        <input
                          type="text"
                          value={synonym}
                          onChange={(e) => handleInputChange('synonym', e.target.value, index)}
                          className="form-input"
                        />
                        <button
                          onClick={() => removeSynonym(index)}
                          className="remove-synonym-btn"
                          disabled={currentEditWord.synonyms.length <= 1}
                        >
                          X
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addSynonym}
                      className="add-synonym-btn"
                    >
                      + 동의어 추가
                    </button>
                  </div>
                  
                  <div className="edit-buttons">
                    <button
                      onClick={saveEditedWord}
                      className="save-btn"
                    >
                      저장
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setCurrentEditWord(null);
                      }}
                      className="cancel-btn"
                    >
                      취소
                    </button>
                  </div>
                </div>
              )}
              
              {/* 편집 버튼 */}
              {!editMode && (
                <button
                  onClick={toggleEditMode}
                  className="edit-button"
                  title="단어 편집"
                >
                  ✎
                </button>
              )}
            </div>

            {/* 통합된 컨트롤 영역 */}
            {!editMode && (
              <div className="controls">
                <button
                  onClick={prevCard}
                  className="control-button"
                  disabled={editMode}
                >
                  ◀
                </button>
                
                <button
                  onClick={toggleAutoPlay}
                  className={`control-button ${isAutoPlay ? 'pause' : 'play'}`}
                  title={isAutoPlay ? '자동재생 중지' : '자동재생 시작'}
                  disabled={editMode}
                >
                  {isAutoPlay ? '⏸' : '▶'}
                </button>
                
                <button
                  onClick={shuffleCards}
                  className="control-button shuffle"
                  title="카드 섞기"
                  disabled={editMode}
                >
                  🔀
                </button>
                
                <button
                  onClick={markAsUnknown}
                  className="control-button unknown"
                  title="모르는 단어로 표시"
                  disabled={editMode}
                >
                  ✕
                </button>
                
                <button
                  onClick={nextCard}
                  className="control-button"
                  disabled={editMode}
                >
                  ▶
                </button>
              </div>
            )}

            {/* 진행 상태 */}
            {!editMode && (
              <div className="progress">
                {currentData.length > 0 ? `${currentIndex + 1} / ${currentData.length}` : '0 / 0'}
              </div>
            )}

            {/* 힌트 */}
            {!isAutoPlay && !editMode && (
              <p className="hint">
                카드를 클릭하면 정답을 볼 수 있습니다
              </p>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <p className="empty-message">
              {studyMode === 'unknown' 
                ? '모르는 단어로 표시한 항목이 없습니다.' 
                : Object.keys(wordData).length === 0
                  ? '단어 데이터가 없습니다. 단어장을 업로드하거나 만드세요.'
                  : !wordData[targetLang] || !wordData[targetLang][synonymLang]
                    ? `${targetLang}에서 ${synonymLang}로의 단어 데이터가 없습니다.`
                    : '현재 카테고리에 단어가 없습니다.'}
            </p>
          </div>
        )}
      </main>

      {/* 푸터 */}
      <footer className="app-footer">
        다국어 스피드 암기 학습 앱 © 2025
      </footer>
    </div>
  );
};

export default FlashcardApp;